<script setup>
import { reactive, computed, watch, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { DirectUpload } from 'activestorage';
import * as XLSX from 'xlsx';

import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';
import WhatsAppTemplateParser from 'dashboard/components-next/whatsapp/WhatsAppTemplateParser.vue';
import CampaignMessageEditor from './CampaignMessageEditor.vue';

const props = defineProps({
  campaign: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['submit', 'cancel']);

function normalizePhone(raw) {
  if (!raw) return null;
  const stripped = raw.trim();
  const hasPlus = stripped.startsWith('+');
  const digits = stripped.replace(/\D/g, '');
  if (digits.length < 8) return null;
  if (hasPlus) return `+${digits}`;
  if (digits.startsWith('55') && digits.length >= 12 && digits.length <= 13)
    return `+${digits}`;
  if (digits.length >= 10 && digits.length <= 11) return `+55${digits}`;
  return `+${digits}`;
}

function parsePhoneText(text) {
  return text
    .split(/[\n,;]+/)
    .map(s => s.trim())
    .filter(Boolean)
    .map(normalizePhone)
    .filter(Boolean);
}

const { t } = useI18n();

const formState = {
  uiFlags: useMapGetter('campaigns/getUIFlags'),
  labels: useMapGetter('labels/getLabels'),
  inboxes: useMapGetter('inboxes/getWhatsAppInboxes'),
  getFilteredWhatsAppTemplates: useMapGetter(
    'inboxes/getFilteredWhatsAppTemplates'
  ),
  accountId: useMapGetter('getCurrentAccountId'),
  currentUser: useMapGetter('getCurrentUser'),
};

const initialState = {
  title: '',
  inboxId: null,
  templateId: null,
  message: '',
  scheduledAt: null,
  selectedAudience: [],
  audienceType: 'labels',
  phoneListText: '',
  sheetId: '',
  minDelay: 5,
  maxDelay: 15,
  showAdvanced: false,
};

const buildStateFromCampaign = campaign => {
  if (!campaign) return { ...initialState };
  const phoneListEntry = campaign.audience?.find(a => a.type === 'PhoneList');
  return {
    title: campaign.title || '',
    inboxId: campaign.inbox_id || null,
    templateId: campaign.template_params?.template_id || null,
    message: campaign.message || '',
    scheduledAt: campaign.scheduled_at
      ? new Date(campaign.scheduled_at).toISOString().slice(0, 16)
      : null,
    selectedAudience: phoneListEntry
      ? []
      : campaign.audience?.map(a => a.id).filter(Boolean) || [],
    audienceType: phoneListEntry ? 'phone_list' : 'labels',
    phoneListText: phoneListEntry
      ? (phoneListEntry.phones || []).join('\n')
      : '',
    sheetId: campaign.additional_attributes?.sheet_id || '',
    minDelay: campaign.additional_attributes?.min_delay ?? 5,
    maxDelay: campaign.additional_attributes?.max_delay ?? 15,
    showAdvanced: false,
  };
};

const state = reactive(buildStateFromCampaign(props.campaign));
const fileInputRef = ref(null);
const templateParserRef = ref(null);

const parsedPhones = computed(() => parsePhoneText(state.phoneListText));

const formatDuration = seconds => {
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  if (m < 60) return s > 0 ? `${m}min ${s}s` : `${m}min`;
  const h = Math.floor(m / 60);
  const rm = m % 60;
  return rm > 0 ? `${h}h ${rm}min` : `${h}h`;
};

const estimatedTime = computed(() => {
  const count = parsedPhones.value.length;
  if (count < 2) return null;
  const intervals = count - 1;
  const min = Number(state.minDelay) || 1;
  const max = Math.max(Number(state.maxDelay) || 1, min);
  const minTotal = intervals * min;
  const maxTotal = intervals * max;
  return {
    min: formatDuration(minTotal),
    max: formatDuration(maxTotal),
    count,
  };
});

const audienceValid = computed(() => {
  if (state.audienceType === 'phone_list') return parsedPhones.value.length > 0;
  return state.selectedAudience?.length > 0;
});

const selectedInbox = computed(
  () =>
    formState.inboxes.value?.find(inbox => inbox.id === state.inboxId) || null
);

const isEvolutionInbox = computed(() => {
  const provider = selectedInbox.value?.provider;
  return provider === 'evolution_api' || provider === 'evolution';
});

const rules = computed(() => ({
  title: { required, minLength: minLength(1) },
  inboxId: { required },
  templateId: isEvolutionInbox.value ? {} : { required },
  message: isEvolutionInbox.value ? { required, minLength: minLength(1) } : {},
  scheduledAt: { required },
  selectedAudience: {},
}));

const v$ = useVuelidate(rules, state);

const isCreating = computed(() => formState.uiFlags.value.isCreating);

const currentDateTime = computed(() => {
  // Added to disable the scheduled at field from being set to the current time
  const now = new Date();
  const localTime = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return localTime.toISOString().slice(0, 16);
});

const mapToOptions = (items, valueKey, labelKey) =>
  items?.map(item => ({
    value: item[valueKey],
    label: item[labelKey],
  })) ?? [];

const audienceList = computed(() =>
  mapToOptions(formState.labels.value, 'id', 'title')
);

const inboxOptions = computed(() =>
  mapToOptions(formState.inboxes.value, 'id', 'name')
);

const templateOptions = computed(() => {
  if (!state.inboxId) return [];
  const templates = formState.getFilteredWhatsAppTemplates.value(state.inboxId);
  return templates.map(template => {
    // Create a more user-friendly label from template name
    const friendlyName = template.name
      .replace(/_/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase());

    return {
      value: template.id,
      label: `${friendlyName} (${template.language || 'en'})`,
      template: template,
    };
  });
});

const selectedTemplate = computed(() => {
  if (!state.templateId) return null;
  return templateOptions.value.find(option => option.value === state.templateId)
    ?.template;
});

const getErrorMessage = (field, errorKey) => {
  const baseKey = 'CAMPAIGN.WHATSAPP.CREATE.FORM';
  return v$.value[field].$error ? t(`${baseKey}.${errorKey}.ERROR`) : '';
};

const formErrors = computed(() => ({
  title: getErrorMessage('title', 'TITLE'),
  inbox: getErrorMessage('inboxId', 'INBOX'),
  template: getErrorMessage('templateId', 'TEMPLATE'),
  message: v$.value.message?.$error ? 'A mensagem é obrigatória' : '',
  scheduledAt: getErrorMessage('scheduledAt', 'SCHEDULED_AT'),
  audience: getErrorMessage('selectedAudience', 'AUDIENCE'),
}));

const hasRequiredTemplateParams = computed(() => {
  return templateParserRef.value?.v$?.$invalid === false || true;
});

const isSubmitDisabled = computed(
  () =>
    v$.value.$invalid ||
    !hasRequiredTemplateParams.value ||
    !audienceValid.value
);

const formatToUTCString = localDateTime =>
  localDateTime ? new Date(localDateTime).toISOString() : null;

const resetState = () => {
  Object.assign(state, { ...initialState });
  v$.value.$reset();
};

const downloadTemplate = () => {
  const wb = XLSX.utils.book_new();
  const data = [
    ['Nome', 'Telefone'],
    ['João Silva', '(31) 97104-5800'],
    ['Maria Souza', '31987654321'],
    ['Carlos Lima', '+5521991234567'],
    ['Ana Costa', '5511999887766'],
  ];
  const ws = XLSX.utils.aoa_to_sheet(data);
  ws['!cols'] = [{ wch: 30 }, { wch: 20 }];
  XLSX.utils.book_append_sheet(wb, ws, 'Contatos');
  XLSX.writeFile(wb, 'modelo_lista_numeros.xlsx');
};

const handleFileUpload = event => {
  const file = event.target.files[0];
  if (!file) return;
  const reader = new FileReader();
  const isXlsx = /\.(xlsx|xls|ods)$/i.test(file.name);

  reader.onload = e => {
    let numbers = [];
    if (isXlsx) {
      const wb = XLSX.read(e.target.result, { type: 'array' });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
      // Find the "Telefone" column index (case-insensitive), fallback to column index 0
      const header = rows[0]?.map(h => String(h).toLowerCase().trim()) ?? [];
      const phoneCol = header.findIndex(
        h => h.includes('telefone') || h.includes('phone') || h.includes('fone')
      );
      let col = 0;
      if (phoneCol >= 0) col = phoneCol;
      else if (header.findIndex(h => h.includes('nome')) >= 0) col = 1;
      numbers = rows
        .slice(1)
        .map(row =>
          String(row[col] ?? '')
            .replace(/[^0-9+() -]/g, '')
            .trim()
        )
        .filter(Boolean);
    } else {
      numbers = e.target.result
        .split(/[\n,;\r\t]+/)
        .map(s => s.replace(/[^0-9+() -]/g, '').trim())
        .filter(Boolean);
    }
    state.phoneListText = state.phoneListText
      ? `${state.phoneListText}\n${numbers.join('\n')}`
      : numbers.join('\n');
  };

  if (isXlsx) {
    reader.readAsArrayBuffer(file);
  } else {
    reader.readAsText(file);
  }
  event.target.value = '';
};

const handleCancel = () => emit('cancel');

// Campaigns have no conversation/message to attach the header file to (it's
// reused across every recipient), so the file is uploaded up front to get a
// permanent public URL instead of relying on a message attachment.
const uploadHeaderMediaFile = file => {
  return new Promise((resolve, reject) => {
    const upload = new DirectUpload(
      file,
      `/api/v1/accounts/${formState.accountId.value}/direct_uploads`,
      {
        directUploadWillCreateBlobWithXHR: xhr => {
          xhr.setRequestHeader(
            'api_access_token',
            formState.currentUser.value.access_token
          );
        },
      }
    );

    upload.create((error, blob) => {
      if (error) {
        reject(error);
        return;
      }
      const filename = encodeURIComponent(blob.filename);
      resolve(
        `${window.location.origin}/rails/active_storage/blobs/redirect/${blob.signed_id}/${filename}`
      );
    });
  });
};

const prepareCampaignDetails = (mediaUrl = null) => {
  let messageContent;
  let templateParams;

  if (isEvolutionInbox.value) {
    messageContent = state.message;
    templateParams = {};
  } else {
    const currentTemplate = selectedTemplate.value;
    const parserData = templateParserRef.value;
    messageContent = parserData?.renderedTemplate || '';
    const processedParams = parserData?.processedParams || {};
    if (mediaUrl && processedParams.header) {
      processedParams.header = {
        ...processedParams.header,
        media_url: mediaUrl,
      };
    }
    templateParams = {
      name: currentTemplate?.name || '',
      namespace: currentTemplate?.namespace || '',
      category: currentTemplate?.category || 'UTILITY',
      language: currentTemplate?.language || 'en_US',
      processed_params: processedParams,
    };
  }

  return {
    title: state.title,
    message: messageContent,
    template_params: templateParams,
    inbox_id: state.inboxId,
    scheduled_at: formatToUTCString(state.scheduledAt),
    audience:
      state.audienceType === 'phone_list'
        ? [{ type: 'PhoneList', phones: parsedPhones.value }]
        : state.selectedAudience?.map(id => ({ id, type: 'Label' })),
    additional_attributes: {
      ...(state.sheetId ? { sheet_id: state.sheetId } : {}),
      min_delay: Number(state.minDelay) || 5,
      max_delay: Number(state.maxDelay) || 15,
    },
  };
};

const handleSubmit = async () => {
  const isFormValid = await v$.value.$validate();
  if (!isFormValid) return;

  let mediaUrl = null;
  const uploadedFile = templateParserRef.value?.uploadedFile;
  if (uploadedFile) {
    try {
      mediaUrl = await uploadHeaderMediaFile(uploadedFile);
    } catch (error) {
      useAlert(t('CAMPAIGN.WHATSAPP.CREATE.FORM.MEDIA_UPLOAD_ERROR'));
      return;
    }
  }

  emit('submit', prepareCampaignDetails(mediaUrl));
  resetState();
  handleCancel();
};

// Reset template/message when inbox changes
watch(
  () => state.inboxId,
  () => {
    state.templateId = null;
    state.message = '';
  }
);
</script>

<!-- eslint-disable vue/no-bare-strings-in-template -->
<template>
  <form class="flex flex-col gap-4" @submit.prevent="handleSubmit">
    <Input
      v-model="state.title"
      :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.TITLE.LABEL')"
      :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.TITLE.PLACEHOLDER')"
      :message="formErrors.title"
      :message-type="formErrors.title ? 'error' : 'info'"
    />

    <div class="flex flex-col gap-1">
      <label for="inbox" class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.INBOX.LABEL') }}
      </label>
      <ComboBox
        id="inbox"
        v-model="state.inboxId"
        :options="inboxOptions"
        :has-error="!!formErrors.inbox"
        :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.INBOX.PLACEHOLDER')"
        :message="formErrors.inbox"
        class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
      />
    </div>

    <!-- Template selector (apenas para caixas não-Evolution) -->
    <div v-if="!isEvolutionInbox" class="flex flex-col gap-1">
      <label for="template" class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.TEMPLATE.LABEL') }}
      </label>
      <ComboBox
        id="template"
        v-model="state.templateId"
        :options="templateOptions"
        :has-error="!!formErrors.template"
        :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.TEMPLATE.PLACEHOLDER')"
        :message="formErrors.template"
        class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
      />
      <p class="mt-1 text-xs text-n-slate-11">
        {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.TEMPLATE.INFO') }}
      </p>
    </div>

    <!-- Template Parser (apenas para caixas não-Evolution) -->
    <WhatsAppTemplateParser
      v-if="selectedTemplate && !isEvolutionInbox"
      ref="templateParserRef"
      :template="selectedTemplate"
    />

    <!-- Campo livre de mensagem (apenas para caixas Evolution API) -->
    <div v-if="isEvolutionInbox" class="flex flex-col gap-1">
      <label class="mb-0.5 text-sm font-medium text-n-slate-12">Mensagem</label>
      <CampaignMessageEditor
        v-model="state.message"
        placeholder="Digite a mensagem que será enviada aos contatos..."
        :has-error="!!formErrors.message"
        :message="formErrors.message"
      />
    </div>

    <div class="flex flex-col gap-2">
      <label class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.LABEL') }}
      </label>
      <!-- Toggle Labels / Lista de números -->
      <div class="flex gap-2">
        <button
          type="button"
          class="px-3 py-1 rounded text-xs font-medium border transition-colors"
          :class="[
            state.audienceType === 'labels'
              ? 'bg-n-brand text-white border-n-brand'
              : 'bg-n-alpha-2 text-n-slate-11 border-n-weak hover:bg-n-alpha-3',
          ]"
          @click="state.audienceType = 'labels'"
        >
          Por Labels
        </button>
        <button
          type="button"
          class="px-3 py-1 rounded text-xs font-medium border transition-colors"
          :class="[
            state.audienceType === 'phone_list'
              ? 'bg-n-brand text-white border-n-brand'
              : 'bg-n-alpha-2 text-n-slate-11 border-n-weak hover:bg-n-alpha-3',
          ]"
          @click="state.audienceType = 'phone_list'"
        >
          Lista de Números
        </button>
      </div>

      <!-- Labels -->
      <TagMultiSelectComboBox
        v-if="state.audienceType === 'labels'"
        v-model="state.selectedAudience"
        :options="audienceList"
        :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.LABEL')"
        :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.PLACEHOLDER')"
        :has-error="state.audienceType === 'labels' && !audienceValid"
        class="[&>div>button]:bg-n-alpha-black2"
      />

      <!-- Lista de números -->
      <div
        v-if="state.audienceType === 'phone_list'"
        class="flex flex-col gap-2"
      >
        <textarea
          v-model="state.phoneListText"
          rows="5"
          placeholder="Cole os números aqui, um por linha ou separados por vírgula.&#10;Ex: (31) 97104-5800&#10;31987654321&#10;+5531991234567"
          class="w-full rounded border border-n-weak bg-n-alpha-black2 px-3 py-2 text-sm text-n-slate-12 placeholder-n-slate-9 focus:outline-none focus:ring-1 focus:ring-n-brand resize-y"
        />
        <div class="flex items-center justify-between gap-2">
          <span class="text-xs text-n-slate-11">
            {{ parsedPhones.length }} número(s) válido(s) detectado(s)
          </span>
          <div class="flex gap-2">
            <button
              type="button"
              class="inline-flex items-center gap-1 rounded border border-n-weak bg-n-alpha-2 px-3 py-1 text-xs text-n-slate-11 hover:bg-n-alpha-3"
              @click="downloadTemplate"
            >
              <span class="i-lucide-download w-3 h-3" />
              Baixar modelo
            </button>
            <label class="cursor-pointer">
              <input
                ref="fileInputRef"
                type="file"
                accept=".csv,.txt,.xlsx,.xls,.ods"
                class="hidden"
                @change="handleFileUpload"
              />
              <span
                class="inline-flex items-center gap-1 rounded border border-n-weak bg-n-alpha-2 px-3 py-1 text-xs text-n-slate-11 hover:bg-n-alpha-3 cursor-pointer"
              >
                <span class="i-lucide-upload w-3 h-3" />
                Importar planilha
              </span>
            </label>
          </div>
        </div>
        <p
          v-if="state.audienceType === 'phone_list' && !audienceValid"
          class="text-xs text-n-red-9"
        >
          Adicione ao menos um número válido.
        </p>
      </div>
    </div>

    <Input
      v-model="state.scheduledAt"
      :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.SCHEDULED_AT.LABEL')"
      type="datetime-local"
      :min="currentDateTime"
      :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.SCHEDULED_AT.PLACEHOLDER')"
      :message="formErrors.scheduledAt"
      :message-type="formErrors.scheduledAt ? 'error' : 'info'"
    />

    <!-- Opções Avançadas -->
    <div
      class="flex flex-col gap-1 border border-n-weak rounded-lg overflow-hidden"
    >
      <button
        type="button"
        class="flex items-center justify-between w-full px-4 py-2.5 text-sm font-medium text-n-slate-11 hover:bg-n-alpha-2 transition-colors"
        @click="state.showAdvanced = !state.showAdvanced"
      >
        <span class="flex items-center gap-2">
          <span class="i-lucide-settings-2 w-4 h-4" />
          Opções Avançadas
        </span>
        <span
          class="w-4 h-4 transition-transform"
          :class="[
            state.showAdvanced
              ? 'i-lucide-chevron-up'
              : 'i-lucide-chevron-down',
          ]"
        />
      </button>
      <div
        v-if="state.showAdvanced"
        class="flex flex-col gap-4 px-4 pb-4 pt-1 border-t border-n-weak bg-n-alpha-1"
      >
        <p class="text-xs text-n-slate-10 mt-1">
          Para reduzir o risco de bloqueios no WhatsApp, as mensagens são
          enviadas uma a uma com um atraso aleatório entre esses valores.
        </p>
        <div class="flex gap-3">
          <div class="flex flex-col gap-1 flex-1">
            <label class="text-xs font-medium text-n-slate-11"
              >Atraso mínimo (segundos)</label
            >
            <input
              v-model.number="state.minDelay"
              type="number"
              min="1"
              max="300"
              class="w-full rounded border border-n-weak bg-n-alpha-black2 px-3 py-2 text-sm text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-brand"
            />
          </div>
          <div class="flex flex-col gap-1 flex-1">
            <label class="text-xs font-medium text-n-slate-11"
              >Atraso máximo (segundos)</label
            >
            <input
              v-model.number="state.maxDelay"
              type="number"
              min="1"
              max="300"
              class="w-full rounded border border-n-weak bg-n-alpha-black2 px-3 py-2 text-sm text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-brand"
            />
          </div>
        </div>
        <div
          v-if="estimatedTime"
          class="flex items-center gap-2 rounded-lg bg-n-brand/10 px-3 py-2.5"
        >
          <span class="i-lucide-clock w-4 h-4 text-n-brand flex-shrink-0" />
          <span class="text-xs text-n-slate-11">
            Tempo estimado para
            <span class="font-semibold text-n-slate-12"
              >{{ estimatedTime.count }} contatos</span
            >:
            <span class="font-semibold text-n-brand">{{
              estimatedTime.min
            }}</span>
            <template v-if="estimatedTime.min !== estimatedTime.max">
              a
              <span class="font-semibold text-n-brand">{{
                estimatedTime.max
              }}</span>
            </template>
          </span>
        </div>
        <p
          v-else-if="
            state.audienceType === 'phone_list' && parsedPhones.length === 0
          "
          class="text-xs text-n-slate-10 italic"
        >
          Adicione contatos na lista para ver o tempo estimado.
        </p>
        <div class="flex flex-col gap-1">
          <label class="text-xs font-medium text-n-slate-11"
            >ID da Planilha Google (opcional)</label
          >
          <input
            v-model="state.sheetId"
            type="text"
            placeholder="Ex: 146YCBKl7YDdopAqcdPP4QJ7dknNAc1RJdOUbQRh4d_w"
            class="w-full rounded border border-n-weak bg-n-alpha-black2 px-3 py-2 text-sm text-n-slate-12 placeholder-n-slate-9 focus:outline-none focus:ring-1 focus:ring-n-brand"
          />
          <p class="text-xs text-n-slate-10">
            Quando informado, as respostas dos contatos serão registradas
            automaticamente na planilha.
          </p>
        </div>
      </div>
    </div>

    <div class="flex gap-3 justify-between items-center w-full">
      <Button
        variant="faded"
        color="slate"
        type="button"
        :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.BUTTONS.CANCEL')"
        class="w-full bg-n-alpha-2 text-n-blue-11 hover:bg-n-alpha-3"
        @click="handleCancel"
      />
      <Button
        :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.BUTTONS.CREATE')"
        class="w-full"
        type="submit"
        :is-loading="isCreating"
        :disabled="isCreating || isSubmitDisabled"
      />
    </div>
  </form>
</template>
